import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Typography,
} from '@mui/material';
import ClipboardDocumentIcon from '@heroicons/react/24/solid/ClipboardDocumentIcon';
import { FunctionComponent } from 'react';
import { useCopyToClipboard } from '@/pages/widgetEditor/hooks/useCopyToClipboard';
import { ResolvedWidget } from '@/pages/widgetEditor/types';
import {
    buildCombinedSnippet,
    buildSnippetAttributes,
    PageConfigElement,
} from '@/pages/widgetEditor/utils';

interface EmbedCodeCardProps {
    resolvedWidgets: ResolvedWidget[];
    /** Absolute URL of the selected version's module entry. */
    entryUrl?: string;
    pageConfig?: PageConfigElement;
}

/** The copyable embed snippet for everything the user has placed. */
export const EmbedCodeCard: FunctionComponent<EmbedCodeCardProps> = ({
    resolvedWidgets,
    entryUrl,
    pageConfig,
}) => {
    const copyToClipboard = useCopyToClipboard();

    const snippetInstances = resolvedWidgets.map(({ widget, config }) => ({
        widget,
        attributes: buildSnippetAttributes(widget, config),
    }));
    const embedSnippet = entryUrl
        ? buildCombinedSnippet(snippetInstances, entryUrl, pageConfig)
        : '';

    return (
        <Card>
            <CardContent
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography variant='h6'>Einbettungscode</Typography>
                <Button
                    size='small'
                    variant='outlined'
                    disabled={!embedSnippet}
                    startIcon={
                        <ClipboardDocumentIcon
                            style={{ width: 18, height: 18 }}
                        />
                    }
                    onClick={() => copyToClipboard(embedSnippet)}
                >
                    Code kopieren
                </Button>
            </CardContent>
            <Divider sx={{ width: '100%' }} />

            {embedSnippet ? (
                <Box
                    component='pre'
                    sx={{
                        m: 0,
                        p: 3,
                        overflowX: 'auto',
                        fontFamily: 'monospace',
                        fontSize: 13,
                        whiteSpace: 'pre',
                        bgcolor: 'grey.50',
                    }}
                >
                    {embedSnippet}
                </Box>
            ) : (
                <Box sx={{ p: 3 }}>
                    <Typography color='text.secondary' variant='body2'>
                        Füge Widgets hinzu, um den Einbettungscode zu erzeugen.
                    </Typography>
                </Box>
            )}
        </Card>
    );
};
